import axios, {
	type AxiosInstance,
	type InternalAxiosRequestConfig,
	type AxiosResponse,
	type GenericAbortSignal,
} from "axios";

type InternalApiArgs = {
	apiUrl: string;
	timezoneOffset?: number;
	token?: string;
};

type RequestOptions = {
	signal?: GenericAbortSignal;
};

export class InternalApi {
	private readonly http: AxiosInstance;

	constructor(args: InternalApiArgs) {
		const authHeader = args.token ? `Bearer ${args.token}` : null;
		const timezoneOffset = (args.timezoneOffset ?? 0) * -1;

		this.http = axios.create({
			baseURL: `${args.apiUrl}/api`,
			withCredentials: true,
		});

		this.http.interceptors.request.use(attachAuthHeader(authHeader));
		this.http.interceptors.request.use(attachTimezoneHeader(timezoneOffset));
		this.http.interceptors.request.use(attachDefaultHeaders());
		this.http.interceptors.response.use(verboseResponse(), verboseError());
	}

	async get(path: string, params = {}, options: RequestOptions = {}) {
		const { data } = await this.http.get(path.toString(), {
			params,
			...options,
		});
		return data;
	}

	async post(path: string, body = {}, options: RequestOptions = {}) {
		const { data } = await this.http.post(path.toString(), body, options);
		return data;
	}

	async put(path: string, body = {}, options: RequestOptions = {}) {
		const { data } = await this.http.put(path.toString(), body, options);
		return data;
	}
}

// ---

const attachAuthHeader =
	(token: string | null) => (config: InternalAxiosRequestConfig) => {
		config.headers.Authorization = token;
		return config;
	};

const attachTimezoneHeader =
	(timezoneOffset: number) => (config: InternalAxiosRequestConfig) => {
		config.headers["X-Timezone-Offset"] = timezoneOffset;

		return config;
	};

const attachDefaultHeaders =
	() =>
	(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
		config.headers.Accept = "application/json";
		config.headers["Content-Type"] = "application/json";

		return config;
	};

const verboseResponse = () => (res: AxiosResponse) => {
	const { status, data } = res;
	console.log(`Response Body (${status}):`, data);
	return res;
};

const verboseError = () => (error: unknown) => {
	if (axios.isAxiosError(error)) {
		if (error.response) {
			const { status, data } = error.response;
			console.log(`Response Body (${status}):`, data); // FIXME: this should be an error/warn log; and revisit it to make sure it is actually required.
		} else {
			console.log("Error:", error.message); // FIXME: this should be an error/warn log;
		}
	}

	return Promise.reject(error);
};
